import './Storage.css';
import React, { useEffect, useCallback } from 'react';
import { useNavigate, Link } from "react-router-dom";
import { connect } from 'react-redux';
import { saveAs } from 'file-saver';
import { 
    BsCardImage,
    BsFileEarmarkExcel,
} from "react-icons/bs";
import { 
    AiFillFileUnknown,
    AiFillFileZip,
    AiOutlineFilePdf,
} from "react-icons/ai";
import {
    GrDocumentTxt,
    GrDocumentCsv,
} from "react-icons/gr";
import { 
    FaRegFilePowerpoint,
    FaFolder, 
    FaFolderOpen,
    FaFolderPlus,
    FaCloudUploadAlt,
} from "react-icons/fa";
import {
    IoCopy,
    IoEyeSharp,
} from "react-icons/io5";
import {
    MdEdit,
} from "react-icons/md";
import TreeView, { flattenTree } from "react-accessible-treeview";
import { withTranslate } from 'react-redux-multilingual';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CreateNewFolderTwoToneIcon from '@mui/icons-material/CreateNewFolderTwoTone';

import DirectoryInput from '../../blocks/DirectoryInput/DirectoryInput';
import DocViewerDialog from '../../blocks/DocViewerDialog/DocViewerDialog'; 

import { 
    GetStorage,
    DeleteStorage,
    DownloadStorage,
    AddNewSubDirectory,
    UpdateDirectory,
    UploadFiles,
    ConvertFilePath,
} from '../../../services/Storage.service';

import {
    FormatDate,
} from '../../../libraries/Tool';

const innerHeight = window.innerHeight;

const validExtensions = ["jpeg","jpg","png","zip","txt","pptx","ppt","pdf","xlsx","xls","csv"];

function Storage(props) {
    const { translate, SnackbarAlert, ConfirmDialog } = props;
    const navigate = useNavigate();
    const [storages, setStoreges] = React.useState(flattenTree({
        path: "",
        name: "",
        size: 0,
        type: "directory",
        children: []
    }));
    const [size, setSize] = React.useState(0);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [expandedIds, setExpandedIds] = React.useState([]);
    const [searchStorage, setSearchStorage] = React.useState("");
    const [mode, setMode] = React.useState(0); // 1: Add new sub directory, 2: Edit directory
    const [path, setPath] = React.useState("");
    const [directoryName, setDirectoryName] = React.useState("");
    const [progress, setProgress] = React.useState(0);
    const [openDocViewerDialog, setOpenDocViewerDialog] = React.useState(false);

    const inputFileFieldRef = React.useRef(null);
    const selectedFilesRef = React.useRef(undefined);
    const docsRef = React.useRef();
    const fileNameRef = React.useRef();

    const onInit = ({ backDrop }) => {
        if(backDrop) props.dispatch({ type: 'UPDATE_BACKDROP', BackDrop: true });
        GetStorage()
        .then(async res => {
            // console.log(res);
            setStoreges(flattenTree(res.data.tree));
            setSize(res.data.size);
        })
        .catch(err => {
            navigate("/signin");
        })
        .finally(() => {
            setTimeout(() => {
                props.dispatch({ type: 'UPDATE_BACKDROP', BackDrop: false });
            }, 1000);
        })
    }

    const FolderIcon = ({ isOpen }) =>
        isOpen 
        ? (<FaFolderOpen color="e8a87c" className="icon" />)
        : (<FaFolder color="e8a87c" className="icon" />)
    ;

    const FileIcon = ({ filename }) => {
        const extension = filename.slice(filename.lastIndexOf(".") + 1);
        switch (extension.toLowerCase()) {
            case "jpeg":
            case "jpg":
            case "png":
                return <BsCardImage className="icon" />;
            case "zip":
                return <AiFillFileZip className="icon" />;
            case "txt":
                return <GrDocumentTxt className="icon" />;
            case "pptx":
            case "pptm":
            case "ppt":
                return <FaRegFilePowerpoint className="icon" />;
            case "pdf":
                return <AiOutlineFilePdf className="icon" />;
            case "xlsx":
            case "xls":
                return <BsFileEarmarkExcel className="icon" />;
            case "csv":
                return <GrDocumentCsv className="icon" />;
            default:
                return <AiFillFileUnknown className="icon" />;
        }
    };

    const MultiSelectDirectoryTreeView = useCallback(
        () => (
            <div>
                <div className="ide">
                    <TreeView
                        data={storages}
                        aria-label="directory tree"
                        clickAction="EXCLUSIVE_SELECT"
                        multiSelect
                        defaultSelectedIds={selectedIds}
                        defaultExpandedIds={expandedIds}
                        onSelect={({ treeState }) => setSelectedIds(Array.from(treeState.selectedIds))}
                        onExpand={({ treeState }) => setExpandedIds(Array.from(treeState.expandedIds))}
                        onKeyDown={e => e.stopPropagation()}
                        nodeRenderer={({
                            element,
                            isBranch,
                            isExpanded,
                            getNodeProps,
                            level,
                            isSelected,
                            handleExpand,
                            handleSelect,
                        }) => (
                            (element.name.toLowerCase().indexOf(searchStorage.toLowerCase()) > -1) &&
                            <>
                                {(((element.path !== path) && (mode === 2)) || (mode !== 2)) && 
                                <div 
                                    {...getNodeProps({ onClick: () => {} })}
                                    style={{ 
                                        paddingLeft: 20 * (level - 1), 
                                        display: "flex", 
                                        alignItems: "center", 
                                        justifyContent: "space-between" 
                                    }}
                                >
                                    <Box 
                                        className='directory' 
                                        title={ translate('createddatetime') + ": " + FormatDate(element.mtime, "yyyy-MM-dd HH:mm:ss")} 
                                        onClick={handleSelect}
                                    >
                                        {(element.type === 'directory') 
                                        ?
                                        <div onClick={handleExpand}>
                                            <FolderIcon isOpen={isExpanded}/>
                                        </div>
                                        : 
                                        <div>
                                            <FileIcon filename={element.name}/>
                                        </div>}
                                        <span onClick={handleExpand}>{element.name}</span>
                                        {element.type === 'directory' && <MdEdit className='icon edit' title={translate('editdirectoryname')} onClick={() => handleEditDirectoryName(element.path, element.name)} />}
                                        {element.type === 'file' && <IoEyeSharp className='icon view' title={translate('viewfile')} onClick={() => handleOpenDocViewerDialog(element.path)}/>}
                                    </Box>
                                    <Box className='options' sx={{ alignItems: "center", gap: "5px" }}>
                                        <Box className='dir-actions'>
                                            {element.type === 'directory' && 
                                            <>
                                                <FaCloudUploadAlt className='icon' title={translate('uploadfilestodirectory')} onClick={() => handleUploadFilesToDirectory(element.path)}/>
                                                <FaFolderPlus className='icon' title={translate('addnewsubdirectory')} onClick={() => handleAddNewSubDirectory(element.path)}/>
                                            </>}
                                            <IoCopy className="icon" title={translate('copypath')} onClick={() => handleCopyPath(element.path)}/>
                                        </Box>
                                        <Typography variant="caption" display="block" className='dir-type'>
                                            {element.type.charAt(0).toUpperCase() + element.type.slice(1)}
                                        </Typography>
                                        <Typography variant="caption" display="block" className='dir-size'>
                                            {(element.size / Math.pow(1024, 2)).toFixed(1)}&nbsp;MB
                                        </Typography>
                                    </Box>
                                </div>}
                                {((mode === 1) && (path !== "/tmp") && (element.type === 'directory') && (element.path === path)) && 
                                <DirectoryInput 
                                    padding={`1px 0 1px 40px`}
                                    isOpen={isExpanded}
                                    value={directoryName}
                                    onClickSave={handleClickSave}
                                    onClickCancel={handleClickCancel}
                                />}
                                {((mode === 2) && (element.type === 'directory') && (element.path === path)) && 
                                <DirectoryInput 
                                    padding={`1px 0 1px 20px`}
                                    isOpen={isExpanded}
                                    value={directoryName}
                                    onClickSave={handleClickSave}
                                    onClickCancel={handleClickCancel}
                                />}
                            </>
                        )}
                    />
                </div>
            </div>  
        ),
        [storages, searchStorage, mode, path, directoryName],
    )

    const handleCloseSnackbarAlert = () => {
        props.dispatch({ type: 'UPDATE_SNACKBARALERT', SnackbarAlert: { ...SnackbarAlert, open: false } });
    }

    const handleCopyPath = (path) => {
        navigator.clipboard.writeText(path);
        props.dispatch({ 
            type: 'UPDATE_SNACKBARALERT', 
            SnackbarAlert: {
                open: true,
                severity: 'success',
                message: translate('copiedtoclipboard'),
                handleClose: () => handleCloseSnackbarAlert()
            } 
        });
    }

    const handleDelete = async () => {
        if(selectedIds.length > 0) {
            let selectedPaths = await selectedIds.map(id => storages[id]?.path);
            props.dispatch({ 
                type: 'UPDATE_CONFIRMDIALOG', 
                ConfirmDialog: {
                    open: true,
                    title: translate('delete'),
                    message: translate('deletedirectoryconfirm'),
                    buttonName: translate('delete'),
                    onClose: () => onCloseConfirmDialog(),
                    onAgree: () => onAgreeConfirmDelete(selectedPaths)
                } 
            });
        }
    }

    const onCloseConfirmDialog = () => {
        props.dispatch({ type: 'UPDATE_CONFIRMDIALOG', ConfirmDialog: {...ConfirmDialog, open: false} });
    }
    
    const onAgreeConfirmDelete = (selectedPaths) => {
        let event;
        onCloseConfirmDialog();
        event = DeleteStorage(selectedPaths);
        event?.then(res => {
            setTimeout(() => {
                props.dispatch({ 
                    type: 'UPDATE_SNACKBARALERT', 
                    SnackbarAlert: {
                        open: true,
                        severity: res?.data?.status,
                        message: translate(`${res?.data?.msg}`),
                        handleClose: () => handleCloseSnackbarAlert()
                    } 
                });
                setSelectedIds([]);
                onInit({ backDrop: false });
            }, 1000)
        })
        .catch(err => {
            setTimeout(() => {
                props.dispatch({ 
                    type: 'UPDATE_SNACKBARALERT', 
                    SnackbarAlert: {
                        open: true,
                        severity: err?.response?.data?.status,
                        message: translate(`${err?.response?.data?.msg}`),
                        handleClose: () => handleCloseSnackbarAlert()
                    } 
                });
            }, 1000)
        })
    }

    const handleDownload = async () => {
        let event;
        if(selectedIds.length > 0) {
            props.dispatch({ type: 'UPDATE_BACKDROP', BackDrop: true });
            let selectedPaths = await selectedIds.map(id => storages[id].path);
            event = DownloadStorage(selectedPaths);
            event?.then(res => {
                // console.log(res)
                let filename = res.headers['content-disposition'].split(';')[1].split('=')[1].replace(/\"/g, '');
                saveAs(res.data, filename);
            })
            .catch(err => {
                setTimeout(() => {
                    props.dispatch({ 
                        type: 'UPDATE_SNACKBARALERT', 
                        SnackbarAlert: {
                            open: true,
                            severity: err?.response?.data?.status,
                            message: translate(`${err?.response?.data?.msg}`),
                            handleClose: () => handleCloseSnackbarAlert()
                        } 
                    });
                }, 1000)
            })
            .finally(() => {
                props.dispatch({ type: 'UPDATE_BACKDROP', BackDrop: false });
            })
        }
    }

    const handleAddNewSubDirectory = (path) => {
        if(path) {
            setPath(path);
            setDirectoryName("");
            setMode(1);
        }
    }

    const handleEditDirectoryName = (path, name) => {
        if(path && name) {
            setPath(path);
            setDirectoryName(name);
            setMode(2);
        }
    }

    const handleClickSave = (value) => {
        setDirectoryName(value);
        switch (mode) {
            case 1:
                if(path && value) {
                    AddNewSubDirectory(path, value)
                    .then(res => {
                        setTimeout(() => {
                            props.dispatch({ 
                                type: 'UPDATE_SNACKBARALERT', 
                                SnackbarAlert: {
                                    open: true,
                                    severity: res?.data?.status,
                                    message: translate(`${res?.data?.msg}`),
                                    handleClose: () => handleCloseSnackbarAlert()
                                } 
                            });
                            setMode(0);
                            onInit({ backDrop: false });
                        }, 1000)
                    })
                    .catch(err => {
                        setTimeout(() => {
                            props.dispatch({ 
                                type: 'UPDATE_SNACKBARALERT', 
                                SnackbarAlert: {
                                    open: true,
                                    severity: err?.response?.data?.status,
                                    message: translate(`${err?.response?.data?.msg}`),
                                    handleClose: () => handleCloseSnackbarAlert()
                                } 
                            });
                        }, 1000)
                    })
                }
                break;
            case 2:
                if(path && value) {
                    UpdateDirectory(path, value)
                    .then(res => {
                        setTimeout(() => {
                            props.dispatch({ 
                                type: 'UPDATE_SNACKBARALERT', 
                                SnackbarAlert: {
                                    open: true,
                                    severity: res?.data?.status,
                                    message: translate(`${res?.data?.msg}`),
                                    handleClose: () => handleCloseSnackbarAlert()
                                } 
                            });
                            setMode(0);
                            onInit({ backDrop: false });
                        }, 1000)
                    })
                    .catch(err => {
                        setTimeout(() => {
                            props.dispatch({ 
                                type: 'UPDATE_SNACKBARALERT', 
                                SnackbarAlert: {
                                    open: true,
                                    severity: err?.response?.data?.status,
                                    message: translate(`${err?.response?.data?.msg}`),
                                    handleClose: () => handleCloseSnackbarAlert()
                                } 
                            });
                        }, 1000)
                    })
                }
                break;
        }
        
    }

    const handleClickCancel = () => {
        setMode(0);
        setPath("");
        setDirectoryName("");
    }

    const handleUploadFilesToDirectory = (path) => {
        // console.log(path);
        if(path) {
            props.dispatch({ 
                type: 'UPDATE_CONFIRMDIALOG', 
                ConfirmDialog: {
                    open: true,
                    title: translate('upload'),
                    message: translate('uploadfilestodirectoryconfirm', { name: path }),
                    buttonName: translate('ok'),
                    onClose: () => onCloseConfirmDialog(),
                    onAgree: () => onAgreeConfirmUploadFilesToDirectory(path)
                } 
            });
        }
    }

    const onAgreeConfirmUploadFilesToDirectory = (path) => {
        setPath(path);
        onCloseConfirmDialog();
        inputFileFieldRef.current?.click();
    }

    const selectFile = (event) => {
        selectedFilesRef.current = event.target.files;
        if (selectedFilesRef.current.length > 0) {
            upload();
        }
    };

    const upload = () => {
        if(!isValidFilesUploaded(selectedFilesRef.current)) {
            props.dispatch({ 
                type: 'UPDATE_SNACKBARALERT', 
                SnackbarAlert: {
                    open: true,
                    severity: "error",
                    message: translate('msg_fileextensionsarenotallowed'),
                    handleClose: () => handleCloseSnackbarAlert()
                } 
            });
            return;
        }
        setProgress(0);
        UploadFiles(selectedFilesRef.current, path, (event) => {
          setProgress(Math.round((100 * event.loaded) / event.total));
        })
        .then((res) => {
            // console.log(res.data)
            setProgress(0);
            setTimeout(() => {
                props.dispatch({ 
                    type: 'UPDATE_SNACKBARALERT', 
                    SnackbarAlert: {
                        open: true,
                        severity: res?.data?.status,
                        message: translate(`${res?.data?.msg}`),
                        handleClose: () => handleCloseSnackbarAlert()
                    } 
                });
                onInit({ backDrop: true });
            }, 1000);
        })
        .catch((err) => {
            // console.log(err);
            setProgress(0);
            setTimeout(() => {
                props.dispatch({ 
                    type: 'UPDATE_SNACKBARALERT', 
                    SnackbarAlert: {
                        open: true,
                        severity: err?.response?.data?.status,
                        message: translate(`${err?.response?.data?.msg}`),
                        handleClose: () => handleCloseSnackbarAlert()
                    } 
                });
            }, 1000)
        })
        .finally(() => inputFileFieldRef.current.value = null)
    };

    const isValidFilesUploaded = (files = [])=>{
        for (const file of files) {
            let fileExtension = file.name.split('.').pop();
            if(!validExtensions.includes(fileExtension)) return false;
        }
        return true;
    }

    const handleOpenDocViewerDialog = async (path) => {
        let uri, paths = path?.split('/');
        fileNameRef.current = paths[paths.length  - 1];
        uri = await new Promise((resolve, reject) => {
            ConvertFilePath(path)
            .then((res) => {
                // console.log(res.data)
                resolve(`${process.env.REACT_APP_PROTOCOL}${process.env.REACT_APP_DOMAIN_NAME}${res.data.newFilePath}`);
            })
            .catch((err) => {
                // console.log(err);
                setTimeout(() => {
                    props.dispatch({ 
                        type: 'UPDATE_SNACKBARALERT', 
                        SnackbarAlert: {
                            open: true,
                            severity: err?.response?.data?.status,
                            message: translate(`${err?.response?.data?.msg}`),
                            handleClose: () => handleCloseSnackbarAlert()
                        } 
                    });
                }, 1000);
                reject(err);
            });
        })
        // console.log(uri);
        if(uri) {
            docsRef.current = [{ uri }];
            setOpenDocViewerDialog(true);
        }
    }

    const handleCloseDocViewerDialog = () => {
        setOpenDocViewerDialog(false);
    }

    useEffect(() => {
        const getData = setTimeout(() => {
            onInit({ backDrop: false });
        }, 500)
        return () => clearTimeout(getData)
    }, [searchStorage])

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        onInit({ backDrop: true });
    }, [])

    return (
        <Container maxWidth="xl">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <input id="icon-button-file" name="multi-files" type="file" style={{"display": "none"}} multiple ref={inputFileFieldRef} onChange={selectFile}/>
                    <Box className='storage-box'>
                        <Grid item xs={12}>
                            <Box sx={{ 
                                    p: 1, 
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderBottom: '1px solid rgb(229, 234, 242)'
                                }}>
                                <Box className='search-box'>
                                    <IconButton type="submit" sx={{ p: '10px' }} aria-label="search">
                                        <SearchIcon />
                                    </IconButton>
                                    <InputBase
                                        type="search"
                                        sx={{ ml: 1, flex: 1 }}
                                        placeholder={translate('search')}
                                        onChange={(e) => setSearchStorage(e.target.value)}
                                    />
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    {selectedIds.length > 0 && <Box>
                                        <Button variant="contained" disableElevation color='error' startIcon={<DeleteOutlineIcon />} onClick={handleDelete}>{translate('delete')}</Button>
                                    </Box>}
                                    {selectedIds.length > 0 && <Box>
                                        <Button variant="contained" disableElevation color='success' startIcon={<CloudDownloadIcon />} onClick={handleDownload}>{translate('download')}</Button>
                                    </Box>}
                                </Box>
                            </Box>
                        </Grid>
                        <Divider sx={{ borderColor: "rgb(249, 249, 249)" }}/>
                        <Box sx={{ overflowY: "auto", height: (innerHeight - 64 - 61 - 18 - 29), position: "relative" }}>
                            <Box className='root-directory'>
                                <Box>
                                    {translate('directoryname')}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: "center", gap: "5px" }}>
                                    <Box className='dir-actions' sx={{ justifyContent: "center", borderLeft: "1px solid rgb(229, 234, 242)" }}>
                                        {translate('actions')}
                                    </Box>
                                    <Box className='dir-type' sx={{ borderLeft: "1px solid rgb(229, 234, 242)" }}>
                                        {translate('type')}
                                    </Box>
                                    <Box className='dir-size' sx={{ textAlign: "center", borderLeft: "1px solid rgb(229, 234, 242)" }}>
                                        {translate('size')}
                                    </Box>
                                </Box>
                            </Box>
                            <Box className='root-directory' sx={{ top: 25 }}>
                                <Box sx={{ fontSize: 18 }}>
                                    <FolderIcon isOpen={true} />tmp
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: "center", gap: "5px" }}>
                                    <Box className='dir-actions'>
                                        <FaCloudUploadAlt className='icon' color='#e31c58' title={translate('uploadfilestodirectory')}onClick={() => handleUploadFilesToDirectory("/tmp")}/>
                                        <FaFolderPlus className='icon' color='#25c742' title={translate('addnewsubdirectory')} onClick={() => handleAddNewSubDirectory("/tmp")}/>
                                        <IoCopy className="icon" color='#1a73e8' title={translate('copypath')} onClick={() => handleCopyPath("/tmp")}/>
                                    </Box>
                                    <Typography variant="caption" display="block" className='dir-type'>
                                        {translate('directory')}
                                    </Typography>
                                    <Typography variant="caption" display="block" className='dir-size'>
                                        {(size / Math.pow(1024, 3)).toFixed(1)}&nbsp;GB
                                    </Typography>
                                </Box>
                            </Box>
                            {mode === 1 && path === "/tmp" &&
                            <DirectoryInput
                                padding="5px 20px 5px 40px"
                                isOpen={false}
                                value={directoryName}
                                onClickSave={handleClickSave}
                                onClickCancel={handleClickCancel}
                            />}
                            {storages?.length === 1 &&
                            <Box sx={{ position: "absolute", width: "100%", top: "30%", textAlign: "center" }}>
                                <CreateNewFolderTwoToneIcon sx={{ fontSize: "200px", color: "#4152691a" }} />
                                <Typography variant="h5" sx={{ color: "#9999998c", textAlign: "center" }}>
                                {translate('createyouritemandstartexperimentingnow', { item: `${translate('directories').toLowerCase()}` })}
                                </Typography>
                            </Box>}
                            <MultiSelectDirectoryTreeView />
                        </Box>
                        <Box sx={{ pl: 2, pb: 1 }}>{translate('allowedfilesextensionsareasfollows')}: {validExtensions.toString()}.</Box>
                    </Box>
                </Grid>
            </Grid>
            <DocViewerDialog 
                open={openDocViewerDialog}
                docs={docsRef.current}
                fileName={fileNameRef.current}
                handleClose={handleCloseDocViewerDialog}
            />
        </Container>
    );
}
function mapStateToProps(state) {
    return {
        BackDrop: state.BackDrop,
        SnackbarAlert: state.SnackbarAlert,
        ConfirmDialog: state.ConfirmDialog,
    }
}
export default connect(mapStateToProps)(withTranslate(Storage));